import { useState } from "react";

import { Button, Callout, Colors, FormGroup } from "@blueprintjs/core";
import styled from "@emotion/styled";
import Select from "react-select";

import CreditSVG from "@/../../hypetrain/static/img/credit.svg";
import { pluralize } from "@/components/helpers/string-utils";
import { EmbedSvg } from "@/components/helpers/ui/EmbedSvg";
import { Box, Flex } from "@/components/layout/flexbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { StyledDialog } from "@/components/pieces/StyledDialog";
import { Avatar } from "@/components/pieces/users/Avatar";
import {
  BRAND_PURPLE,
  INNER_BORDER_COLOR,
  LIGHTEST_BACKGROUND,
  SUBTEXT_COLOR,
} from "@/css/constants";
import {
  useAssignWorkspaceGroupMutation,
  useUnassignWorkspaceGroupMutation,
  useWorkspaceGroupsQuery,
} from "@/graphql";
import { SelectOption } from "@/types/select";
import { StatsMember } from "@/types/workspaces";
import { ensureNonNullArray } from "@/utils/array";
import { formatCredits } from "@/utils/string";

export enum ActionType {
  Add = "Add To Group",
  Remove = "Remove From All Groups",
}

interface ResultsProps {
  result: { success?: boolean };
  onClose: () => void;
  actionType: ActionType;
  group?: SelectOption;
}

function Results({ result, onClose, actionType, group }: ResultsProps) {
  const resultView =
    result && result.success ? (
      <Callout intent="success" icon="tick">
        {actionType === ActionType.Add
          ? `Group successfully set to ${group ? group.label : "new group"}`
          : "Remove group successfully"}
      </Callout>
    ) : (
      <Callout intent="warning">
        We could not process your request at this time. Please contact us.
      </Callout>
    );
  return (
    <Box>
      {resultView}
      <Box mt={16}>
        <Button
          intent="primary"
          large={true}
          onClick={() => onClose()}
          loading={false}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
}

interface SetGroupForUsersDialogProps {
  members: StatsMember[];
  selectAllUsers?: boolean;
  excludedUsers?: string[];
  totalRecords?: number;
  open: boolean;
  onClose: () => void;
  afterSetGroup: () => void;
}

export function SetGroupForUsersDialog({
  members,
  selectAllUsers = false,
  excludedUsers = [],
  totalRecords = 0,
  open,
  onClose,
  afterSetGroup,
}: SetGroupForUsersDialogProps) {
  const [result, setResult] = useState<{ success?: boolean }>();
  const [actionType, setActionType] = useState(ActionType.Add);
  const [group, setGroup] = useState<SelectOption>();

  // Calculate the actual number of users that will be affected
  const affectedUsersCount = selectAllUsers
    ? totalRecords - excludedUsers.length
    : members.length;

  const { data: groupData } = useWorkspaceGroupsQuery({
    fetchPolicy: "network-only",
    variables: {
      sortField: "name",
      sortDesc: true,
    },
  });

  const groups = ensureNonNullArray(groupData?.workspaceGroups);

  const options = groups.map((gr) => ({
    value: gr.id,
    label: gr.name,
  }));

  const [assignGroup, { loading }] = useAssignWorkspaceGroupMutation({
    onCompleted: (data) => {
      const { assignWorkspaceGroup } = data;
      setResult({ success: assignWorkspaceGroup?.ok ?? false });
      if (assignWorkspaceGroup?.ok) {
        afterSetGroup();
      }
    },
    onError: () => {
      setResult({ success: false });
    },
  });

  const [unassignGroup, { loading: unassignLoading }] =
    useUnassignWorkspaceGroupMutation({
      onCompleted: (data) => {
        const { unassignWorkspaceGroup } = data;
        setResult({ success: unassignWorkspaceGroup?.ok ?? false });
        if (unassignWorkspaceGroup?.ok) {
          afterSetGroup();
        }
      },
      onError: () => {
        setResult({ success: false });
      },
    });

  return (
    <StyledDialog
      isOpen={open}
      lazy={true}
      title="Set Group"
      width="480px"
      onClose={() => {
        onClose();
      }}
      onOpening={() => {
        setResult(undefined);
        setActionType(ActionType.Add);
        setGroup(undefined);
      }}
    >
      <Box py={3} px={4}>
        {result ? (
          <Results
            result={result}
            onClose={onClose}
            actionType={actionType}
            group={group}
          />
        ) : (
          <Box>
            <Box mb={1} fontWeight={600}>
              {pluralize({
                amount: affectedUsersCount,
                singular: "Member",
                plural: "Members",
                showCount: false,
              })}
            </Box>
            <UserListStyled>
              {members.map((member) => {
                return (
                  <UserGroupRow key={`member-g-${member.id}`} member={member} />
                );
              })}
            </UserListStyled>
            <Box>
              <Box mb={1} fontWeight={600}>
                Action
              </Box>
              <ActionTypeSelector
                onChange={setActionType}
                selectedData={actionType}
              />
            </Box>
            {actionType === ActionType.Add && (
              <Box>
                <FormGroup style={{ margin: 0 }} label={<b>Select Group</b>}>
                  <Select
                    isLoading={loading}
                    value={options.find((o) =>
                      group ? o.value === group.value : null,
                    )}
                    onChange={(newValue) => {
                      if (newValue) {
                        setGroup(newValue);
                      }
                    }}
                    isSearchable={true}
                    options={options}
                  />
                </FormGroup>
              </Box>
            )}
            <Box mt={4}>
              <Button
                intent={actionType === ActionType.Remove ? "danger" : "primary"}
                large={true}
                disabled={loading || (!group && actionType === ActionType.Add)}
                onClick={() => {
                  if (actionType === ActionType.Add) {
                    if (!group?.value) return;

                    assignGroup({
                      variables: {
                        groupId: group.value,
                        memberIds: members.map((member) => {
                          return member.id;
                        }),
                      },
                    });
                  }

                  if (actionType === ActionType.Remove) {
                    unassignGroup({
                      variables: {
                        groupId: group ? group.value : undefined,
                        memberIds: members.map((member) => {
                          return member.id;
                        }),
                      },
                    });
                  }
                }}
                loading={loading || unassignLoading}
              >
                {actionType === ActionType.Remove ? "Remove" : "Confirm"}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </StyledDialog>
  );
}

interface UserGroupRowProps {
  member: StatsMember;
  isShowGroup?: boolean;
  isShowCredits?: boolean;
}

export function UserGroupRow({
  member,
  isShowGroup = true,
  isShowCredits = false,
}: UserGroupRowProps) {
  const { user, groups, availableCredits = "0" } = member;
  if (!user) return null;

  const credits = formatCredits(availableCredits);

  return (
    <Flex
      className="user-group-row"
      justifyContent="space-between"
      alignItems="center"
    >
      <Flex alignItems="center" pr={3}>
        <Avatar size={20} user={user} />
        <Box ml={2}>{user.name || user.email}</Box>
      </Flex>
      {isShowCredits && (
        <Flex alignItems="center">
          <EmbedSvg src={CreditSVG} height={14} width={14} />{" "}
          <Box ml={1}>{credits}</Box>
        </Flex>
      )}
      {isShowGroup && (
        <Flex alignItems="center" color={SUBTEXT_COLOR}>
          {groups.length
            ? `In ${pluralize({
                amount: groups.length,
                singular: "group",
                plural: "groups",
              })}`
            : "Not in any groups"}
        </Flex>
      )}
    </Flex>
  );
}

interface ActionTypeSelectorProps {
  selectedData: ActionType;
  onChange: (action: ActionType) => void;
}

function ActionTypeSelector({
  selectedData,
  onChange,
}: ActionTypeSelectorProps) {
  const actions = [ActionType.Add, ActionType.Remove];
  return (
    <Flex
      bg="#F0EFF8"
      p="2px"
      mb={3}
      className="round-corners"
      style={{ whiteSpace: "nowrap", maxWidth: "100%" }}
    >
      {actions.map((action) => {
        const isSelected = action === selectedData;
        return (
          <Clickable
            key={action}
            onClick={() => {
              onChange(action);
            }}
          >
            <Flex
              width={50 / 100}
              justifyContent="center"
              className="round-corners"
              py={2}
              color={isSelected ? BRAND_PURPLE : SUBTEXT_COLOR}
              bg={isSelected ? Colors.WHITE : "#F0EFF8"}
            >
              {action}
            </Flex>
          </Clickable>
        );
      })}
    </Flex>
  );
}

export const UserListStyled = styled(Box)`
  border-radius: 5px;
  border: solid 1px ${INNER_BORDER_COLOR};
  margin-bottom: 15px;
  max-height: 200px;
  overflow-y: auto;
  overflow-x: hidden;
  .user-group-row {
    padding: 12px;
    &:nth-of-type(even) {
      background-color: ${LIGHTEST_BACKGROUND};
    }
  }
`;
